/**
 * AdminLogin — email + password sign-in for HidroVerse admin tools.
 *
 *   /admin/login
 *
 * Visual theme: matches the main HidroVerse marketing site
 *   · off-white surface (#FAFAF7) with white form card
 *   · ink-navy text (#0C2A40) and cyan accent (#1FB6C1)
 *   · same typography and proportions as the rest of the chrome
 *
 * Two flows in one screen:
 *   · Normal sign-in     — email + password → JWT
 *   · First-time setup   — ADMIN_TOKEN + allow-listed email + new password
 */
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, Link } from "react-router-dom";
import { ShieldCheck, LogIn, KeyRound, ArrowLeft } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_JWT = "hv_admin_jwt";
const STORAGE_LEGACY = "hv_admin_token";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [adminToken, setAdminToken] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [allowed, setAllowed] = useState([]);
  const [anyAdmin, setAnyAdmin] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/admin/status`);
        const d = await r.json();
        if (cancelled) return;
        setAllowed(d.allowed_emails || []);
        setAnyAdmin(!!d.any_admin_exists);
        if (!d.any_admin_exists) setMode("bootstrap");
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const url = mode === "bootstrap"
        ? `${API}/api/admin/bootstrap`
        : `${API}/api/admin/login`;
      const body = mode === "bootstrap"
        ? { admin_token: adminToken.trim(), email: email.trim().toLowerCase(),
            password, name: name.trim() || undefined }
        : { email: email.trim().toLowerCase(), password };
      const r = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) {
        const msg = typeof data.detail === "string" ? data.detail : "Sign-in failed.";
        throw new Error(msg);
      }
      localStorage.setItem(STORAGE_JWT, data.token);
      localStorage.setItem(STORAGE_LEGACY, data.token);
      navigate("/portal/access");
    } catch (e2) {
      setError(e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main
      className="min-h-screen px-6 py-16"
      style={{ background: "#FAFAF7", color: "#0C2A40" }}
      data-testid="admin-login"
    >
      <Helmet>
        <title>{`Admin sign-in — HidroVerse`}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div className="mx-auto w-full" style={{ maxWidth: 460 }}>
        <Link
          to="/"
          data-testid="admin-login-back"
          className="font-mono uppercase inline-flex items-center gap-1.5 mb-8 transition-opacity hover:opacity-70"
          style={{
            fontSize: "0.62rem",
            letterSpacing: "0.22em",
            color: "rgba(12,42,64,0.55)",
            textDecoration: "none",
            fontWeight: 700,
          }}
        >
          <ArrowLeft size={12} /> Back to hidroverse.co.za
        </Link>

        {/* Card */}
        <div
          className="overflow-hidden"
          style={{
            background: "#FFFFFF",
            border: "1px solid rgba(12,42,64,0.08)",
            borderRadius: 14,
            boxShadow:
              "0 1px 2px rgba(12,42,64,0.04), 0 36px 72px -16px rgba(12,42,64,0.18)",
          }}
        >
          {/* Charcoal header bar — matches mega-menu pattern */}
          <div
            className="px-6 py-4 flex items-center gap-3"
            style={{
              background: "rgba(22,24,27,0.92)",
              color: "#F5F7FA",
              borderBottom: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            <span
              className="inline-flex items-center justify-center"
              style={{
                width: 30,
                height: 30,
                borderRadius: 6,
                background: "rgba(31,182,193,0.15)",
                color: "#1FB6C1",
              }}
            >
              <ShieldCheck size={15} strokeWidth={1.75} />
            </span>
            <div>
              <div
                className="font-mono uppercase"
                style={{
                  fontSize: "0.58rem",
                  letterSpacing: "0.28em",
                  color: "#1FB6C1",
                  fontWeight: 700,
                }}
              >
                HidroVerse · admin
              </div>
              <div className="font-display"
                   style={{ fontSize: "0.95rem", fontWeight: 500,
                            color: "rgba(245,247,250,0.95)", marginTop: 1 }}>
                {mode === "bootstrap" ? "First-time setup" : "Sign in"}
              </div>
            </div>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit} className="px-6 py-6 flex flex-col gap-3.5">
            <p
              className="font-light"
              style={{
                fontSize: "0.85rem",
                color: "rgba(12,42,64,0.7)",
                lineHeight: 1.55,
                marginBottom: 4,
              }}
            >
              {mode === "bootstrap"
                ? "Use the master ADMIN_TOKEN from the backend .env plus an allow-listed email to create or reset your admin record."
                : "Sign in with your HidroVerse admin email and password."}
            </p>

            {mode === "bootstrap" && (
              <Field label="Master admin token">
                <input
                  type="password"
                  value={adminToken}
                  onChange={(e) => setAdminToken(e.target.value)}
                  required
                  autoFocus
                  data-testid="admin-login-master-token"
                  className="w-full px-3 py-2.5 outline-none"
                  placeholder="Paste ADMIN_TOKEN from .env"
                  style={inputStyle}
                />
              </Field>
            )}
            <Field label="Email">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus={mode === "login"}
                data-testid="admin-login-email"
                className="w-full px-3 py-2.5 outline-none"
                placeholder="you@hidroverse.co.za"
                style={inputStyle}
              />
            </Field>
            <Field label={mode === "bootstrap" ? "New password (min 8 chars)" : "Password"}>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={mode === "bootstrap" ? 8 : undefined}
                data-testid="admin-login-password"
                className="w-full px-3 py-2.5 outline-none"
                style={inputStyle}
              />
            </Field>
            {mode === "bootstrap" && (
              <Field label="Display name (optional)">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="admin-login-name"
                  className="w-full px-3 py-2.5 outline-none"
                  style={inputStyle}
                />
              </Field>
            )}

            {error && (
              <div className="px-3 py-2"
                   style={{
                     background: "rgba(184,92,56,0.10)",
                     border: "1px solid rgba(184,92,56,0.35)",
                     color: "#B85C38",
                     fontSize: "0.82rem",
                   }}
                   data-testid="admin-login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="admin-login-submit"
              className="mt-2 px-5 py-3 font-mono uppercase inline-flex items-center gap-2 justify-center rounded-full transition-all hover:scale-[1.01]"
              style={{
                background: "#1FB6C1",
                color: "#070A0D",
                fontSize: "0.7rem",
                letterSpacing: "0.18em",
                fontWeight: 700,
                border: "none",
                cursor: busy ? "wait" : "pointer",
                opacity: busy ? 0.5 : 1,
              }}
            >
              {mode === "bootstrap" ? (
                <><KeyRound size={13} /> {busy ? "Creating…" : "Create admin record"}</>
              ) : (
                <><LogIn size={13} /> {busy ? "Signing in…" : "Sign in"}</>
              )}
            </button>

            <button
              type="button"
              data-testid="admin-login-toggle-mode"
              onClick={() => { setError(""); setMode(mode === "login" ? "bootstrap" : "login"); }}
              className="font-mono uppercase mt-1 transition-opacity hover:opacity-70"
              style={{
                background: "transparent",
                border: "none",
                color: "rgba(12,42,64,0.55)",
                fontSize: "0.6rem",
                letterSpacing: "0.20em",
                fontWeight: 700,
                padding: "8px 0",
                cursor: "pointer",
                textDecoration: "underline",
                textUnderlineOffset: 3,
              }}
            >
              {mode === "login"
                ? "First-time setup / forgot password →"
                : "← Back to normal sign-in"}
            </button>
          </form>
        </div>

        {allowed.length > 0 && (
          <div
            className="mt-5 px-4 py-3"
            style={{
              background: "rgba(31,182,193,0.06)",
              border: "1px solid rgba(31,182,193,0.25)",
              borderRadius: 10,
              fontSize: "0.78rem",
              lineHeight: 1.55,
              color: "rgba(12,42,64,0.75)",
            }}
            data-testid="admin-login-allowed"
          >
            <div
              className="font-mono uppercase mb-1"
              style={{
                fontSize: "0.55rem",
                letterSpacing: "0.22em",
                color: "#0E7A82",
                fontWeight: 700,
              }}
            >
              Bootstrap allow-list
            </div>
            Only these emails can run first-time setup:&nbsp;
            <span className="font-mono">{allowed.join(", ")}</span>
            {!anyAdmin && (
              <div className="mt-2 font-mono"
                   style={{ fontSize: "0.72rem", color: "rgba(12,42,64,0.55)" }}>
                No admin records exist yet — run setup to create the first one.
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
}

const inputStyle = {
  background: "#FAFAF7",
  border: "1px solid rgba(12,42,64,0.18)",
  color: "#0C2A40",
  fontSize: "0.92rem",
  borderRadius: 6,
};

function Field({ label, children }) {
  return (
    <label className="block">
      <div
        className="font-mono uppercase mb-1"
        style={{
          fontSize: "0.58rem",
          letterSpacing: "0.22em",
          color: "rgba(12,42,64,0.55)",
          fontWeight: 700,
        }}
      >
        {label}
      </div>
      {children}
    </label>
  );
}
